import { useState, useMemo, useEffect, useRef } from 'react';

import { Check, Lock, ArrowLeft, ChevronDown } from 'lucide-react';

import { useTranslation } from '@app/app/i18n';
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
} from '@app/shared/ui/dropdown-menu';
import { cn } from '@app/app/lib';
import { analytics } from '@app/app/analytics';
import { PlatformName } from '@app/app/analytics/types';

import { CheckoutWidgetProps } from '../types';
import { PAYMENT_METHODS } from '../constants';
import {
  container,
  backButton,
  card,
  cardHeader,
  title,
  subtitle,
  label,
  paymentMethodButton,
  paymentMethodText,
  paymentMethodSubtitle,
  chevronIcon,
  dropdown,
  dropdownItem,
  trustSignals,
  trustSignalItem,
  trustIcon,
  ctaButton,
  ctaSubtext,
  orderSummary,
  planTitle,
  billingPeriodLabel,
  tokensDescription,
  featureIcon,
  featureText,
  divider,
  dividerBorder,
  summaryText,
  totalText,
  totalAmount,
  billingText,
  savingsBadge,
  savingsText,
} from '../styles';

export default function CheckoutWidget({
  planName,
  aiTokens,
  price,
  billingPeriod,
  nextPaymentDate,
  onBackToPricing,
  paypalUrl,
  cardUrl,
  walletsUrl,
  theme = 'light',
  savings,
  onPaymentMethodSelect,
  isLoading = false,
  productId,
  currency,
  productType,
  downloadsCount,
}: CheckoutWidgetProps) {
  const { t } = useTranslation('pricing');
  // Фильтруем доступные методы оплаты на основе переданных URL
  const availablePaymentMethods = useMemo(() => {
    return PAYMENT_METHODS.filter((method) => {
      if (method.id === 'card') return !!cardUrl;
      if (method.id === 'paypal') return !!paypalUrl;
      if (method.id === 'wallets') return !!walletsUrl;
      return false;
    });
  }, [cardUrl, paypalUrl, walletsUrl]);

  // Устанавливаем начальный метод на первый доступный
  const initialPaymentMethod = availablePaymentMethods[0]?.id || 'card';
  const [paymentMethod, setPaymentMethod] = useState(initialPaymentMethod);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  // Синхронизируем выбранный метод с доступными методами
  useEffect(() => {
    const isCurrentMethodAvailable = availablePaymentMethods.some((m) => m.id === paymentMethod);
    if (!isCurrentMethodAvailable && availablePaymentMethods.length > 0) {
      setPaymentMethod(availablePaymentMethods[0].id);
    }
  }, [availablePaymentMethods, paymentMethod]);

  const themeVariant = theme === 'light' ? 'light' : 'dark';

  // Функция для вычисления item_variant
  const getItemVariant = (): string => {
    if (productType === 'ai_tokens') return 'ai_tokens';
    if (billingPeriod === 'yearly') return 'yearly_plan';
    if (billingPeriod === 'monthly') return 'monthly_plan';
    if (productType === 'pay_as_you_go') return 'payg_10';
    return 'monthly_plan';
  };

  const handlePaymentMethodSelect = async (methodId: string) => {
    setPaymentMethod(methodId);

    let urlToUse: string | undefined;

    if (methodId === 'card' && cardUrl) {
      urlToUse = cardUrl;
    } else if (methodId === 'paypal' && paypalUrl) {
      urlToUse = paypalUrl;
    } else if (methodId === 'wallets' && walletsUrl) {
      urlToUse = walletsUrl;
    }

    // Отправляем событие begin_checkout перед переходом в платежную систему
    if (urlToUse) {
      const paymentType = methodId === 'card' || methodId === 'wallets' ? 'stripe' : 'paypal';

      analytics.emitEvent('begin_checkout', {
        [PlatformName.GTM]: {
          ecommerce: {
            currency,
            value: price,
            payment_method: paymentType,
            product_id: productId,
            items: [
              {
                item_name: productType,
                item_variant: getItemVariant(),
                price,
                quantity: 1,
              },
            ],
          },
        },
      });
    }

    if (urlToUse && onPaymentMethodSelect) {
      await onPaymentMethodSelect(methodId, urlToUse);
    } else if (urlToUse) {
      // Fallback to direct redirect if no handler provided
      window.location.href = urlToUse;
    }
  };

  const formatDate = (dateString: string): string => {
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      });
    } catch {
      return dateString;
    }
  };

  const isPayg = productType === 'pay_as_you_go';

  const periodLabel = isPayg
    ? t('checkoutWidget.payAsYouGo')
    : billingPeriod === 'yearly'
      ? t('checkoutWidget.yearlySubscription')
      : t('checkoutWidget.monthlySubscription');

  const billingLabel = isPayg
    ? t('checkoutWidget.oneTime')
    : billingPeriod === 'yearly'
      ? t('checkoutWidget.annually')
      : t('checkoutWidget.monthly');

  return (
    <div ref={containerRef} className={container({ theme: themeVariant })}>
      <div className="w-full flex flex-col">
        {/* Back button */}
        <button
          onClick={onBackToPricing}
          className={backButton({ theme: themeVariant })}
          disabled={isLoading}
        >
          <ArrowLeft size={20} />
          <span>{t('checkoutWidget.backToPlans')}</span>
        </button>

        <div className={card({ theme: themeVariant })}>
          {/* Header */}
          <div className={cardHeader({ theme: themeVariant })}>
            <div className="flex items-start justify-between mb-2">
              <div>
                <h1 className={title({ theme: themeVariant })}>{t('checkoutWidget.title')}</h1>
                <p className={subtitle({ theme: themeVariant })}>{t('checkoutWidget.subtitle')}</p>
              </div>
            </div>
          </div>

          <div className="p-6">
            <div className="grid grid-cols-1 semiMd:grid-cols-2 gap-6">
              {/* Left Column: Order Summary with plan details and features */}
              <div>
                <div className={orderSummary({ theme: themeVariant })}>
                  {/* First row: Monthly/Yearly plan and Change plan */}
                  <div className="flex items-center justify-between mb-2">
                    <p className={billingPeriodLabel({ theme: themeVariant })}>{periodLabel}</p>
                  </div>

                  {/* Plan name */}
                  <h2 className={planTitle({ theme: themeVariant })}>{planName}</h2>

                  {/* Tokens description */}
                  <div className={tokensDescription({ theme: themeVariant })}>
                    <span>{aiTokens}</span>
                    <svg
                      width="16"
                      height="16"
                      viewBox="0 0 20 20"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                      className="w-4 h-4"
                    >
                      <path
                        d="M14.8432 3.19833C14.6892 2.98255 14.4859 2.80663 14.2503 2.68518C14.0146 2.56372 13.7534 2.50024 13.4882 2.5H6.50991C5.97324 2.5 5.46658 2.76083 5.12074 3.24917L1.78741 8.73333C1.69427 8.88634 1.65296 9.06531 1.66963 9.24366C1.68629 9.42201 1.76004 9.59023 1.87991 9.72333L9.37991 18.0567C9.45764 18.144 9.55297 18.214 9.65965 18.2619C9.76633 18.3099 9.88194 18.3347 9.99889 18.3348C10.1158 18.3348 10.2315 18.3102 10.3382 18.2624C10.445 18.2146 10.5404 18.1448 10.6182 18.0575L18.1182 9.72417C18.2384 9.59119 18.3123 9.42295 18.3289 9.24453C18.3456 9.06612 18.3041 8.88709 18.2107 8.73417L14.8432 3.19833V3.19833ZM9.99908 16.2542L4.36991 10H15.6274L9.99908 16.2542ZM3.97991 8.33333L6.50991 4.16667L13.4532 4.115L16.0174 8.33333H3.97991Z"
                        fill="#00BA88"
                      />
                    </svg>
                    <span className="text-general_MI_500_default">AI tokens</span>
                    <span>
                      {isPayg
                        ? ''
                        : billingPeriod === 'yearly'
                          ? `${t('checkoutWidget.perMonth')} (${(aiTokens * 12).toLocaleString()} total)`
                          : t('checkoutWidget.perMonth')}
                    </span>
                  </div>

                  {/* Features list */}
                  <div className="space-y-1.5 mb-4">
                    {[
                      isPayg
                        ? t('checkoutWidget.features.paygDownloads', { count: downloadsCount })
                        : t('checkoutWidget.features.unlimited'),
                      t('checkoutWidget.features.ai'),
                      t('checkoutWidget.features.templates'),
                      t('checkoutWidget.features.branding'),
                    ].map((feature, index) => (
                      <div key={index} className="flex items-start gap-2">
                        <Check size={18} className={featureIcon({ theme: themeVariant })} />
                        <span className={featureText({ theme: themeVariant })}>{feature}</span>
                      </div>
                    ))}
                  </div>

                  {/* Pricing breakdown */}
                  <div className={divider({ theme: themeVariant })}>
                    <div className={`flex justify-between ${summaryText({ theme: themeVariant })}`}>
                      <span>
                        {t('checkoutWidget.subtotal')} ({billingLabel})
                      </span>
                      <span>${price.toFixed(2)}</span>
                    </div>
                    <div className={`flex justify-between ${summaryText({ theme: themeVariant })}`}>
                      <span>{t('checkoutWidget.tax')}</span>
                      <span>$0.00</span>
                    </div>
                    <div
                      className={`flex justify-between items-center pt-1.5 ${dividerBorder({ theme: themeVariant })}`}
                    >
                      <span className={totalText({ theme: themeVariant })}>
                        {t('checkoutWidget.total')}
                      </span>
                      <span className={totalAmount({ theme: themeVariant })}>
                        ${price.toFixed(2)}
                      </span>
                    </div>
                    <div className={billingText({ theme: themeVariant })}>
                      {isPayg
                        ? t('checkoutWidget.oneTimePaymentInfo')
                        : `${t('checkoutWidget.billed')} ${billingLabel}. ${t('checkoutWidget.nextPayment')}: ${formatDate(nextPaymentDate)}`}
                    </div>
                  </div>

                  {/* Savings badge - only for yearly plans */}
                  {savings !== undefined && savings > 0 && (
                    <div className={savingsBadge({ theme: themeVariant })}>
                      <p className={savingsText({ theme: themeVariant })}>
                        💰 {t('checkoutWidget.save', { amount: savings.toFixed(2) })}
                      </p>
                    </div>
                  )}
                </div>
              </div>

              {/* Right Column: Payment method and Trust signals */}
              <div className="space-y-6">
                {/* Payment method selection */}
                <div>
                  <label className={label({ theme: themeVariant })}>
                    {t('checkoutWidget.paymentMethod')}
                  </label>
                  <DropdownMenu open={isDropdownOpen} onOpenChange={setIsDropdownOpen}>
                    <DropdownMenuTrigger asChild>
                      <button
                        className={paymentMethodButton({ theme: themeVariant })}
                        disabled={isLoading}
                      >
                        <div className="text-left">
                          <div className={paymentMethodText({ theme: themeVariant })}>
                            {availablePaymentMethods.find((m) => m.id === paymentMethod)?.name}
                          </div>
                          <div className={paymentMethodSubtitle({ theme: themeVariant })}>
                            {availablePaymentMethods.find((m) => m.id === paymentMethod)?.subtitle}
                          </div>
                        </div>
                        <ChevronDown
                          size={20}
                          className={chevronIcon({ theme: themeVariant, isOpen: isDropdownOpen })}
                        />
                      </button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent
                      className={cn(
                        dropdown({ theme: themeVariant }),
                        'p-0 w-[var(--radix-dropdown-menu-trigger-width)]',
                      )}
                      align="start"
                      sideOffset={8}
                      container={containerRef.current}
                    >
                      {availablePaymentMethods.map((method) => (
                        <DropdownMenuItem
                          key={method.id}
                          onClick={(e) => {
                            e.preventDefault();
                            setIsDropdownOpen(false);
                            handlePaymentMethodSelect(method.id);
                          }}
                          className={cn(
                            dropdownItem({
                              theme: themeVariant,
                              isSelected: paymentMethod === method.id,
                            }),
                            'cursor-pointer',
                          )}
                          disabled={isLoading}
                        >
                          <div className="flex gap-0.5">
                            <div className={paymentMethodText({ theme: themeVariant })}>
                              {method.name}
                            </div>
                            <div className={paymentMethodSubtitle({ theme: themeVariant })}>
                              {method.subtitle}
                            </div>
                          </div>
                        </DropdownMenuItem>
                      ))}
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>

                {/* Trust signals */}
                <div className={trustSignals({ theme: themeVariant })}>
                  <div className={trustSignalItem({ theme: themeVariant })}>
                    <Lock size={18} className={trustIcon({ theme: themeVariant })} />
                    <span className="text-static-text font-medium">
                      {t('checkoutWidget.trustSignals.secure')}
                    </span>
                  </div>
                  <div className={trustSignalItem({ theme: themeVariant })}>
                    <Check size={18} className={trustIcon({ theme: themeVariant })} />
                    <span className="text-static-text">
                      {t('checkoutWidget.trustSignals.guarantee')}
                    </span>
                  </div>
                  <div className={trustSignalItem({ theme: themeVariant })}>
                    <Check size={18} className={trustIcon({ theme: themeVariant })} />
                    <span className="text-static-text">
                      {isPayg
                        ? t('checkoutWidget.trustSignals.noAutoRenewal')
                        : t('checkoutWidget.trustSignals.cancel')}
                    </span>
                  </div>
                </div>

                {/* CTA Button */}
                <div>
                  <button
                    onClick={() => handlePaymentMethodSelect(paymentMethod)}
                    className={ctaButton({ theme: themeVariant })}
                    disabled={isLoading}
                  >
                    {isLoading
                      ? t('checkoutWidget.processing')
                      : t('checkoutWidget.completePurchase')}
                  </button>

                  <p className={ctaSubtext({ theme: themeVariant })}>
                    {t('checkoutWidget.subtext')}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
